import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Menu, X, Crown, Trophy, Users, ArrowRight, Lock, Check,
  Instagram, Music2, MessageCircle, ChevronRight, Flame, Medal,
  ScrollText, Send, Copy, Share2, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { Counter } from "@/components/site/Counter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const InfoTip = ({ text, testid }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-label="Info premio"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-imperygreen/50 text-imperygreen hover:bg-imperygreen/10 transition-colors duration-200"
        >
          <Info size={12} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="bg-black border border-gold/40 text-white text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_fanta-imperyum/artifacts/2bu03nrd_Logo%20con%20foglie%20di%20alloro%20dorate.png";
const HERO_BG = "https://images.unsplash.com/photo-1556056504-5c7696c4c28d?q=80&w=1920&auto=format&fit=crop";
const TROPHY_IMG = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?q=80&w=1200&auto=format&fit=crop";

const NAV = [
  { label: "Come entrare", href: "#come-entrare" },
  { label: "Road Map", href: "#roadmap" },
  { label: "Regolamento", href: "#regolamento" },
  { label: "Premi", href: "#premi" },
  { label: "Community", href: "#community" },
];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// ---------- Nav ----------
const Nav = ({ leagueLink }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button onClick={() => scrollTo("#top")} className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO} alt="FANTA IMPERYUM" className="h-9 md:h-11 w-auto" />
        </button>
        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-link-${n.href.slice(1)}`}
              onClick={() => scrollTo(n.href)}
              className="text-sm tracking-wide text-white/70 hover:text-white transition-colors duration-200"
            >
              {n.label}
            </button>
          ))}
          <a
            href={leagueLink}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-cta-enter"
            className="rounded-full bg-imperygreen px-5 py-2.5 text-sm font-semibold text-black hover:shadow-[0_0_24px_rgba(0,255,102,0.5)] transition-shadow duration-200"
          >
            ENTRA ORA
          </a>
        </nav>
        <button className="md:hidden text-white" onClick={() => setOpen(true)} data-testid="nav-hamburger" aria-label="Menu">
          <Menu size={26} />
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[60] bg-void/95 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-5 h-16">
            <img src={LOGO} alt="FANTA IMPERYUM" className="h-9" />
            <button onClick={() => setOpen(false)} className="text-white" data-testid="mobile-menu-close" aria-label="Chiudi">
              <X size={28} />
            </button>
          </div>
          <div className="flex flex-col gap-2 px-6 pt-10">
            {NAV.map((n, i) => (
              <motion.button
                key={n.href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i }}
                onClick={() => { setOpen(false); setTimeout(() => scrollTo(n.href), 250); }}
                className="text-left font-heading text-3xl text-white py-3 border-b border-white/10"
                data-testid={`mobile-link-${n.href.slice(1)}`}
              >
                {n.label}
              </motion.button>
            ))}
            <a
              href={leagueLink}
              target="_blank"
              rel="noreferrer"
              className="mt-8 text-center rounded-full bg-imperygreen px-6 py-4 font-heading font-bold text-black"
              data-testid="mobile-cta-enter"
            >
              ENTRA NELLA LEGA
            </a>
          </div>
        </motion.div>
      )}
    </header>
  );
};

// ---------- Hero ----------
const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Hero = ({ hero, leagueLink }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const titleWords = hero.title.split(" ");

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/85 to-void" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(0,255,102,0.16),transparent_55%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-6xl px-5 md:px-8 min-h-[100svh] flex flex-col justify-center pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full gold-border px-4 py-1.5 text-[11px] tracking-[0.25em] text-gold uppercase"
        >
          <Crown size={13} /> Lega ufficiale di fantacalcio
        </motion.div>

        <h1 className="font-heading font-black leading-[0.95] tracking-tighter text-[15vw] sm:text-7xl lg:text-8xl">
          {titleWords.map((w, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={heroLine} initial="hidden" animate="show" custom={i} className="block text-gold-shine">
                {w}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="overflow-hidden mt-5">
          <motion.p
            variants={heroLine} initial="hidden" animate="show" custom={titleWords.length}
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-green-glow"
          >
            {hero.subtitle}
          </motion.p>
        </div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-4 max-w-md text-base sm:text-lg text-white/60"
        >
          {hero.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <a
            href={leagueLink} target="_blank" rel="noreferrer" data-testid="hero-cta-enter"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-imperygreen px-8 py-4 font-heading font-bold text-black hover:shadow-[0_0_36px_rgba(0,255,102,0.55)] transition-shadow duration-300"
          >
            {hero.cta_primary}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
          </a>
          <button
            onClick={() => scrollTo("#premi")} data-testid="hero-cta-prizes"
            className="inline-flex items-center justify-center gap-2 rounded-full gold-border px-8 py-4 font-heading font-bold text-gold hover:bg-gold/10 transition-colors duration-200"
          >
            <Trophy size={18} /> {hero.cta_secondary}
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40 text-xs tracking-[0.3em] uppercase animate-pulse">
        Scroll
      </div>
    </section>
  );
};

// ---------- Marquee ----------
const Marquee = () => {
  const items = ["PRESTIGIO", "COMPETIZIONE", "ESCLUSIVITA", "CONQUISTA", "IMPERO", "VETTA"];
  const loop = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-surface py-6" data-testid="marquee">
      <div className="marquee-track">
        {loop.map((t, i) => (
          <span key={i} className="mx-8 font-heading text-3xl md:text-5xl font-bold uppercase text-transparent [-webkit-text-stroke:1px_rgba(212,175,55,0.45)] flex items-center gap-8">
            {t}
            <span className="text-imperygreen text-2xl [-webkit-text-stroke:0]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------- About ----------
const About = ({ about }) => (
  <section className="relative py-24 md:py-32 px-5 md:px-8" data-testid="about-section">
    <div className="mx-auto max-w-5xl">
      <Reveal>
        <span className="text-gold text-xs tracking-[0.3em] uppercase">01 — Il progetto</span>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gold-shine">
          {about.title}
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-8 max-w-3xl text-lg md:text-2xl leading-relaxed text-white/70">
          {about.text}
        </p>
      </Reveal>
    </div>
  </section>
);

// ---------- Steps ----------
const Steps = ({ steps, leagueLink }) => (
  <section id="come-entrare" className="relative py-24 md:py-32 px-5 md:px-8 bg-surface" data-testid="steps-section">
    <div className="mx-auto max-w-7xl">
      <Reveal><span className="text-gold text-xs tracking-[0.3em] uppercase">02 — Onboarding</span></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-heading text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight break-words">
          COME ENTRARE <span className="text-imperygreen">NELL'IMPERYUM</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <Reveal key={i} delay={0.08 * i}>
            <div
              data-testid={`step-card-${i}`}
              className="group relative h-full rounded-2xl glass gold-border p-6 hover:-translate-y-1 transition-transform duration-300"
            >
              <span className="font-heading text-5xl font-black text-white/10 group-hover:text-imperygreen/30 transition-colors duration-300">
                {s.num}
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-white">{s.title}</h3>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-12 flex justify-center">
          <a
            href={leagueLink} target="_blank" rel="noreferrer" data-testid="steps-cta-enter"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-heading font-bold text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow duration-300"
          >
            ENTRA ORA <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

// ---------- Roadmap ----------
const Roadmap = ({ roadmap, prizes }) => {
  const current = roadmap.current_participants;
  const milestones = roadmap.milestones;
  const tiers = prizes?.tiers || [];
  const [openTier, setOpenTier] = useState({});
  const nextIdx = milestones.findIndex((m) => m.participants > current);
  const nextMilestone = nextIdx === -1 ? milestones[milestones.length - 1] : milestones[nextIdx];
  const prevTarget = nextIdx <= 0 ? 0 : milestones[nextIdx - 1].participants;
  const segTotal = nextMilestone.participants - prevTarget;
  const segDone = Math.max(0, current - prevTarget);
  const pct = Math.min(100, Math.round((segDone / segTotal) * 100));

  return (
    <section id="roadmap" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="roadmap-section">
      <div className="mx-auto max-w-7xl">
        <Reveal><span className="text-gold text-xs tracking-[0.3em] uppercase">03 — Traguardi</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gold-shine">
            LA ROAD MAP DELL'IMPERYUM
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-lg md:text-xl text-white/60">{roadmap.subtitle}</p>
        </Reveal>

        {/* progress */}
        <Reveal delay={0.2}>
          <div className="mt-10 rounded-2xl glass gold-border p-6 md:p-8">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-white/50">Partecipanti attuali</p>
                <p className="font-heading text-5xl md:text-6xl font-black text-imperygreen text-green-glow">
                  <Counter to={current} />
                </p>
              </div>
              <div className="text-right">
                <span className="inline-block rounded-full bg-gold/15 gold-border px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-gold">
                  Prossimo obiettivo
                </span>
                <p className="mt-2 font-heading text-2xl md:text-3xl font-bold text-white">
                  {current} / {nextMilestone.participants}
                </p>
              </div>
            </div>
            <div className="mt-5 h-4 w-full overflow-hidden rounded-full bg-white/5 border border-white/10">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-imperygreen-deep via-imperygreen to-gold shadow-[0_0_20px_rgba(0,255,102,0.6)]"
                data-testid="roadmap-progress-bar"
              />
            </div>
          </div>
        </Reveal>

        {/* timeline */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {milestones.map((m, i) => {
            const unlocked = current >= m.participants;
            const isNext = m.participants === nextMilestone.participants && !unlocked;
            const tier = tiers.find((t) => t.participants === m.participants);
            const tierItems = tier?.items || [];
            const top3 = tierItems.slice(0, 3);
            const isOpen = !!openTier[i];
            return (
              <Reveal key={i} delay={0.06 * i}>
                <div
                  data-testid={`milestone-${i}`}
                  className={`relative rounded-2xl p-5 transition-transform duration-300 hover:-translate-y-1 ${
                    unlocked
                      ? "bg-gradient-to-b from-imperygreen/15 to-transparent border border-imperygreen/50 shadow-[0_0_28px_rgba(0,255,102,0.18)]"
                      : isNext
                      ? "glass border border-gold/60 shadow-[0_0_28px_rgba(212,175,55,0.18)]"
                      : "glass border border-white/10 opacity-70"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-2xl font-black text-white">{m.participants}</span>
                    {unlocked ? (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-imperygreen text-black"><Check size={15} /></span>
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5 text-white/40"><Lock size={14} /></span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-white/40">Partecipanti</p>
                  {top3.length > 0 ? (
                    <div className="mt-4 border-t border-white/10 pt-3">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Primi 3 premi</p>
                      <ul className="mt-2 space-y-2">
                        {top3.map((p, pi) => (
                          <li key={pi} data-testid={`milestone-${i}-prize-${pi}`} className="flex items-baseline gap-2 text-sm">
                            <span className="shrink-0 font-heading font-bold text-white/50">{pi + 1}°</span>
                            <span className="font-heading font-bold text-white flex-1 min-w-0 break-words">{p.name}</span>
                            <span className="shrink-0 font-bold text-imperygreen">{p.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-4 border-t border-white/10 pt-3">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Premio</p>
                      <p className="mt-1 font-heading text-lg font-bold text-white break-words">{m.prize}</p>
                    </div>
                  )}
                  {unlocked && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.2em] text-imperygreen font-bold">Sbloccato</span>}
                </div>
                {tierItems.length > 3 && (
                  <button
                    onClick={() => setOpenTier((prev) => ({ ...prev, [i]: !prev[i] }))}
                    data-testid={`milestone-show-more-${i}`}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full gold-border px-5 py-2.5 text-sm font-heading font-bold text-gold hover:bg-gold/10 transition-colors duration-200"
                  >
                    {isOpen ? "Mostra meno" : "Mostra altro"}
                    <ChevronRight size={15} className={`transition-transform duration-200 ${isOpen ? "-rotate-90" : "rotate-90"}`} />
                  </button>
                )}
                {isOpen && (
                  <div className="mt-3 flex flex-col gap-2" data-testid={`milestone-prizes-panel-${i}`}>
                    {tierItems.slice(3).map((p, pi) => (
                      <motion.div
                        key={pi}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.04 * pi, ease: [0.22, 1, 0.36, 1] }}
                        className="flex items-center justify-between gap-2 rounded-xl glass border border-white/10 px-4 py-3 text-sm"
                      >
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-gold">{p.position}</span>
                        <span className={`font-heading font-bold flex-1 min-w-0 truncate text-right ${p.condition ? "text-imperygreen" : "text-white"}`}>{p.name}</span>
                        {p.value ? <span className="shrink-0 font-heading font-black text-imperygreen">{p.value}</span> : null}
                        {p.condition ? <InfoTip text={p.condition} testid={`milestone-${i}-voucher-info-${pi}`} /> : null}
                      </motion.div>
                    ))}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------- Regolamento ----------
const Regolamento = ({ regolamento }) => {
  if (!regolamento) return null;
  const rules = regolamento.rules || [];
  return (
    <section id="regolamento" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="regolamento-section">
      <div className="mx-auto max-w-5xl">
        <Reveal><span className="text-gold text-xs tracking-[0.3em] uppercase">04 — Regole</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-heading text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gold-shine">
            {regolamento.title}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-3xl text-lg text-white/60">{regolamento.text}</p>
        </Reveal>
        <div className="mt-10 grid gap-3">
          {rules.map((r, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <div
                data-testid={`rule-${i}`}
                className="group flex items-start gap-4 rounded-2xl glass gold-border p-5 hover:-translate-y-0.5 transition-transform duration-300"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-imperygreen/15 border border-imperygreen/40 font-heading font-bold text-imperygreen">
                  {i + 1}
                </span>
                <p className="pt-1.5 text-white/80">{r}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- Prizes ----------
const PrizeCard = ({ p, featured, testid }) => (
  <div
    data-testid={testid}
    className={`group h-full rounded-3xl p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 ${
      featured
        ? "gold-border bg-gradient-to-br from-gold/12 via-void to-void shadow-[0_0_34px_rgba(212,175,55,0.16)]"
        : "glass border border-white/10 hover:border-gold/50"
    }`}
  >
    {featured ? <Crown size={30} className="text-gold" /> : <Medal size={28} className="text-gold" />}
    <span className="mt-5 block text-xs uppercase tracking-[0.2em] text-white/50">{p.position}</span>
    <h4 className={`mt-2 font-heading font-black ${featured ? "text-3xl text-gold-shine" : "text-2xl text-white"}`}>{p.name}</h4>
    <p className={`mt-2 font-heading font-black ${featured ? "text-4xl text-white" : "text-3xl text-imperygreen"}`}>{p.value}</p>
    {p.condition ? <p className="mt-4 text-sm text-white/50">{p.condition}</p> : null}
  </div>
);

const PRIZE_VISIBLE = 3;

const PrizeListRow = ({ p, index, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: -14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: 0.06 * index, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testid}
    className="flex items-center justify-between gap-3 rounded-xl glass border border-white/10 px-4 py-3.5 hover:border-gold/40 transition-colors duration-200"
  >
    <div className="flex items-center gap-3 min-w-0">
      <span className="shrink-0 w-28 md:w-36 text-[10px] md:text-xs uppercase tracking-[0.18em] text-gold">{p.position}</span>
      <span className={`font-heading font-bold truncate ${p.condition ? "text-imperygreen" : "text-white"}`}>{p.name}</span>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {p.value ? <span className="font-heading font-black text-imperygreen">{p.value}</span> : null}
      {p.condition ? <InfoTip text={p.condition} testid={`${testid}-info`} /> : null}
    </div>
  </motion.div>
);

const Prizes = ({ prizes }) => {
  const tiers = prizes.tiers || (prizes.items ? [{ participants: null, items: prizes.items }] : []);
  const [expanded, setExpanded] = useState({});
  const toggle = (ti) => setExpanded((prev) => ({ ...prev, [ti]: !prev[ti] }));
  return (
    <section id="premi" className="relative py-24 md:py-32 px-5 md:px-8 bg-surface" data-testid="prizes-section">
      <div className="mx-auto max-w-7xl">
        <Reveal><span className="text-gold text-xs tracking-[0.3em] uppercase">05 — Montepremi</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gold-shine">
            {prizes.title}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-lg text-white/50">Ad ogni traguardo raggiunto si sbloccano nuovi premi. Piu cresce l'Imperyum, piu grande diventa la posta in palio.</p>
        </Reveal>

        <div className="mt-14 space-y-16">
          {tiers.map((tier, ti) => {
            const items = tier.items || [];
            const isExp = !!expanded[ti];
            const top = items.slice(0, PRIZE_VISIBLE);
            const rest = items.slice(PRIZE_VISIBLE);
            return (
              <div key={ti} data-testid={`prize-tier-${ti}`}>
                <Reveal>
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-black">
                      <Trophy size={14} /> Traguardo
                    </span>
                    {tier.participants != null && (
                      <span className="font-heading text-2xl md:text-3xl font-black text-white">
                        {tier.participants} <span className="text-white/40 text-lg font-bold uppercase tracking-wide">partecipanti</span>
                      </span>
                    )}
                  </div>
                </Reveal>
                <div className="mt-6 h-px w-full bg-gradient-to-r from-gold/40 via-white/10 to-transparent" />
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                  {top.map((p, i) => (
                    <Reveal key={i} delay={0.08 * i}>
                      <PrizeCard p={p} featured={i === 0} testid={`prize-card-${ti}-${i}`} />
                    </Reveal>
                  ))}
                </div>
                {isExp && rest.length > 0 && (
                  <div className="mt-5 flex flex-col gap-2.5" data-testid={`prize-list-${ti}`}>
                    {rest.map((p, i) => (
                      <PrizeListRow key={i} p={p} index={i} testid={`prize-row-${ti}-${i + PRIZE_VISIBLE}`} />
                    ))}
                  </div>
                )}
                {rest.length > 0 && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => toggle(ti)}
                      data-testid={`show-more-${ti}`}
                      className="group inline-flex items-center gap-2 rounded-full gold-border px-7 py-3 font-heading font-bold text-gold hover:bg-gold/10 transition-colors duration-200"
                    >
                      {isExp ? (
                        <>Mostra meno <ChevronRight size={17} className="-rotate-90 transition-transform duration-200" /></>
                      ) : (
                        <>Mostra altro <ChevronRight size={17} className="rotate-90 transition-transform duration-200" /></>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------- Growth / FOMO ----------
// ---------- Share Dialog ----------
const ShareDialog = ({ open, onOpenChange, community }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const msg = "Entra con me in FANTA IMPERYUM - Non partecipi. Conquisti!";
  const options = [
    { label: "WhatsApp", icon: MessageCircle, color: "#25D366", href: `https://wa.me/?text=${encodeURIComponent(msg + " " + shareUrl)}` },
    { label: "Telegram", icon: Send, color: "#229ED9", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(msg)}` },
    { label: "Instagram", icon: Instagram, color: "#E1306C", href: community.instagram || "#" },
    { label: "TikTok", icon: Music2, color: "#ffffff", href: community.tiktok || "#" },
  ];
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass gold-border sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black text-gold-shine text-left">Condividi l'Imperyum</DialogTitle>
          <DialogDescription className="text-sm text-white/60 text-left">
            Invita i tuoi amici: piu siete, piu grandi diventano i premi.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {options.map((o) => (
            <a
              key={o.label} href={o.href} target="_blank" rel="noreferrer" data-testid={`share-${o.label.toLowerCase()}`}
              className="group flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4 py-4 font-heading font-bold text-white hover:border-gold/50 hover:bg-white/5 transition-[border-color,background-color] duration-200"
            >
              <o.icon size={20} style={{ color: o.color }} /> {o.label}
            </a>
          ))}
        </div>
        <button
          onClick={copy} data-testid="share-copy"
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-imperygreen px-4 py-3.5 font-heading font-bold text-black hover:shadow-[0_0_24px_rgba(0,255,102,0.5)] transition-shadow duration-200"
        >
          {copied ? <><Check size={18} /> Link copiato!</> : <><Copy size={18} /> Copia il link</>}
        </button>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Growth / FOMO ----------
const Growth = ({ growth, community }) => {
  const [shareOpen, setShareOpen] = useState(false);
  return (
    <section className="relative py-24 md:py-32 px-5 md:px-8 overflow-hidden" data-testid="growth-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,255,102,0.1),transparent_60%)]" />
      <div className="relative mx-auto max-w-5xl text-center">
        <Reveal>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            PIU PARTECIPANTI <span className="text-imperygreen text-green-glow">→</span> PREMI PIU GRANDI
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 mx-auto max-w-2xl text-lg md:text-xl text-white/60">{growth.text}</p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex items-center justify-center gap-4 md:gap-10">
            {[100, 250, 500].map((n, i) => (
              <div key={n} className="flex items-center gap-4 md:gap-10">
                <div className="text-center">
                  <Counter to={n} className="font-heading text-3xl md:text-5xl font-black text-gold-shine" />
                  <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/40 mt-1">partecipanti</p>
                </div>
                {i < 2 && <ChevronRight className="text-imperygreen" size={24} />}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <button
            onClick={() => setShareOpen(true)} data-testid="growth-cta"
            className="group mt-12 inline-flex items-center gap-2 rounded-full bg-imperygreen px-9 py-4 font-heading font-bold text-black hover:shadow-[0_0_36px_rgba(0,255,102,0.55)] transition-shadow duration-300"
          >
            <Share2 size={18} /> {growth.cta}
          </button>
        </Reveal>
      </div>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} community={community} />
    </section>
  );
};

// ---------- Community ----------
const Community = ({ community }) => (
  <section id="community" className="relative py-24 md:py-32 px-5 md:px-8 bg-surface" data-testid="community-section">
    <div className="mx-auto max-w-5xl text-center">
      <Reveal><span className="text-gold text-xs tracking-[0.3em] uppercase">05 — Community</span></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">{community.title}</h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-5 mx-auto max-w-2xl text-lg text-white/60">{community.text}</p>
      </Reveal>

      <Reveal delay={0.2}>
        <a
          href={community.whatsapp} target="_blank" rel="noreferrer" data-testid="community-whatsapp"
          className="group mt-10 flex w-full items-center justify-center gap-3 rounded-2xl bg-[#25D366] px-8 py-6 font-heading text-lg md:text-2xl font-black text-black hover:shadow-[0_0_40px_rgba(37,211,102,0.5)] transition-shadow duration-300"
        >
          <MessageCircle size={26} /> ENTRA NELLA COMMUNITY WHATSAPP
        </a>
      </Reveal>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <Reveal delay={0.28}>
          <a
            href={community.instagram} target="_blank" rel="noreferrer" data-testid="community-instagram"
            className="group flex items-center justify-center gap-3 rounded-2xl glass gold-border px-6 py-6 font-heading font-bold text-white hover:bg-white/5 transition-colors duration-200"
          >
            <Instagram size={22} className="text-gold" /> INSTAGRAM
          </a>
        </Reveal>
        <Reveal delay={0.34}>
          <a
            href={community.tiktok} target="_blank" rel="noreferrer" data-testid="community-tiktok"
            className="group flex items-center justify-center gap-3 rounded-2xl glass gold-border px-6 py-6 font-heading font-bold text-white hover:bg-white/5 transition-colors duration-200"
          >
            <Music2 size={22} className="text-gold" /> TIKTOK
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

// ---------- Footer ----------
const Footer = ({ footer }) => (
  <footer className="relative overflow-hidden pt-24 pb-10 px-5 md:px-8 border-t border-white/10" data-testid="footer">
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <img src={LOGO} alt="FANTA IMPERYUM" className="h-14 w-auto" />
          <p className="mt-4 text-gold text-sm tracking-wide">{footer.tagline}</p>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {NAV.map((n) => (
            <button key={n.href} onClick={() => scrollTo(n.href)} className="text-sm text-white/60 hover:text-white transition-colors duration-200" data-testid={`footer-link-${n.href.slice(1)}`}>
              {n.label}
            </button>
          ))}
        </div>
      </div>
      <div className="pointer-events-none select-none mt-16 font-heading font-black tracking-tighter text-[18vw] leading-none text-white/[0.03] text-center">
        IMPERYUM
      </div>
      <div className="mt-6 border-t border-white/10 pt-6 text-center text-xs text-white/40">
        © 2026 FANTA IMPERYUM. Tutti i diritti riservati.
      </div>
    </div>
  </footer>
);

// ---------- Page ----------
export default function Landing() {
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => setErr(true));
  }, []);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-void text-center px-6" data-testid="landing-error">
        <img src={LOGO} alt="FANTA IMPERYUM" className="h-16" />
        <p className="text-white/60">Impossibile caricare i contenuti. Riprova piu tardi.</p>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-void">
        <img src={LOGO} alt="FANTA IMPERYUM" className="h-16 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-void text-white">
      <Nav leagueLink={c.league_link} />
      <Hero hero={c.hero} leagueLink={c.league_link} />
      <Marquee />
      <About about={c.about} />
      <Steps steps={c.steps} leagueLink={c.league_link} />
      <Roadmap roadmap={c.roadmap} prizes={c.prizes} />
      <Regolamento regolamento={c.regolamento} />
      <Prizes prizes={c.prizes} />
      <Growth growth={c.growth} community={c.community} />
      <Community community={c.community} />
      <Footer footer={c.footer} />
    </div>
  );
}
